/* eslint-disable max-len */
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ViewContainerRef,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { filter, map, take } from 'rxjs';
import { BrnDialogService } from '@spartan-ng/brain/dialog';
import { DownloadProgressHostComponent } from '../ui/download';
import { ThemeService } from '@lite-workspace/shared/theme';
import { ThemeToggleComponent } from '@lite-workspace/shared/blocks/theme-toggle';
import { IconComponent } from '@lite-workspace/shared/blocks/icon';
import { HlmAppShellImports, type NavItem } from '@lite-workspace/shared/helm-ui/app-shell';
import { RouteVeilComponent } from '@lite-workspace/shared/helm-ui/app-root';
import { HlmProductSwitcherImports } from '@lite-workspace/shared/helm-ui/product-switcher';
import { HlmTopbar } from '@lite-workspace/shared/helm-ui/topbar';
import { HlmButton } from '@lite-workspace/shared/helm-ui/button';
import { HlmBottomSheet } from '@lite-workspace/shared/helm-ui/bottom-sheet';
import { HlmDropdownMenuImports } from '@lite-workspace/shared/helm-ui/dropdown-menu';
import { IS_MOBILE } from '@lite-workspace/utils';
import { PreviewBannerComponent } from '../ui/preview-banner.component';
import { ProjectDownloadButtonComponent } from '../features/projects/ui/project-download-button.component';
import { ProjectDownloadLauncherService } from '../features/projects/ui/project-download-launcher.service';
import { OrganizerShareLauncherService } from '../features/projects/ui/organizer-share-launcher.service';
import { AccountMenuComponent, ThemeSegmentComponent, type ThemeSegmentMode } from '@lite-workspace/shared/blocks/account-menu';
import { AccountMenuHostComponent } from '../features/account/ui/account-menu.component';
import { NotificationsBellComponent } from '@lite-workspace/shared/blocks/notifications';
import { BalancePopupsLauncherService } from '../features/account/ui/balance-popups-launcher.service';
import { ProjectsFacade } from '../features/projects/domain/application/projects.facade';
import { DefaultPrintProductService } from '../features/projects/domain/application/default-print-product.service';
import { isDemoProject } from '../features/projects/domain/utils/project.utils';
import { withTimeout } from '../features/projects/domain/utils/with-timeout.util';
import { OPEN_FORMS } from '../features/projects/domain/utils/comment-forms';
import { pluralRu } from '@lite-workspace/shared/format';
import { ACCESS_READ_LIMIT_MS } from '../core/route-wait';
import {
  CreateProjectDialogComponent,
  type CreateProjectDialogContext,
  type CreateProjectDraft,
} from '../features/projects/ui/create-project-dialog.component';
import { TemplateGrantedLauncherService } from '../features/designs/ui/template-granted-launcher.service';
import { RetouchDoneLauncherService } from '../core/retouch-done-launcher.service';
import { RetouchAccessService } from '../core/retouch-access.service';
import { ProjectRoleService } from '../core/project-role';
import { PartnerStudioResource } from '../features/partner/domain/infrastructure/partner-cabinet.resource';
import { clearAuth } from '../core/auth';
import { bffLogout, forceRedirectToBffLogin, isBffMode } from '../core/bff';
import { SupportWidgetService } from '@lite-workspace/shared/support-widget';
import { AuthAnalyticsService } from '../features/auth/auth-analytics.service';
import { UiAnalyticsService } from '../core/ui-analytics.service';
import { CreateEntryService } from '../core/create-entry.service';
import { OnboardingStateService } from '../core/onboarding-state.service';
import { LessonsDoorComponent, OnboardingAnchorDirective, OnboardingService } from '@lite-workspace/shared/onboarding-service';
import { AlbumsCoachState } from '../features/projects/feature/albums-coach-state';
import { resumeNavigation } from '../core/onboarding.guard';
import { WizardChromeService } from '@lite-workspace/shared/blocks/wizard-shell';
import { environment } from '@environment';
import { ProjectCreationService } from '../features/projects/domain/application/project-creation.service';
import { CreateProjectPreferencesService } from '../features/projects/domain/application/create-project-preferences.service';
import { CreateProjectDraftStashService } from '../features/projects/domain/application/create-project-draft-stash.service';
import { motionDurationMs } from '@lite-workspace/shared/helm-ui/utils';

/** Съёмка — проект больше чем с таким числом фотографий: по ним онбординг и отличает новичка. */
const SHOOT_PHOTOS = 10;

@Component({
  selector: 'app-shell',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    DownloadProgressHostComponent,
    IconComponent,
    HlmAppShellImports,
    HlmProductSwitcherImports,
    HlmTopbar,
    HlmButton,
    HlmBottomSheet,
    HlmDropdownMenuImports,
    PreviewBannerComponent,
    ProjectDownloadButtonComponent,
    OnboardingAnchorDirective,
    LessonsDoorComponent,
    AccountMenuHostComponent,
    AccountMenuComponent,
    NotificationsBellComponent,
    ThemeSegmentComponent,
    ThemeToggleComponent,
    RouteVeilComponent,
  ],
  template: `
    <hlm-app-shell #shell [tabs]="shellTabs()" [hideHeader]="hideStandardHeader()" (tabClicked)="onTabClick($event)">
      @if (mobileProjectBar()) {
        <!-- ═══ MOBILE in-project bar (Variant B): ☰ + ‹ | centered section pill | avatar ═══ -->
        <header
          hlmAppShellHeader
          hlmTopbar="cabinet"
          class="sticky top-0 z-20 h-[var(--app-shell-header-h)]"
          [class.scrolled]="shell.scrolled()"
        >
          <div class="relative flex h-full items-center px-2">
            <button
              hlmBtn
              variant="ghost"
              size="icon"
              type="button"
              class="pointer-coarse:size-11"
              (click)="navSheetOpen.set(true)"
              aria-label="Меню проекта"
            >
              <app-icon name="menu" [size]="18" />
            </button>
            <a
              hlmBtn
              variant="ghost"
              size="icon"
              class="pointer-coarse:size-11"
              routerLink="/projects"
              aria-label="К списку проектов"
            >
              <app-icon name="chevron-left" [size]="17" />
            </a>
            <!-- the pill sits on the TRUE viewport mid-line — positioned off the full-width bar, not the leftover flex space -->
            <button
              type="button"
              [hlmDropdownMenuTrigger]="sectionMenu"
              align="center"
              side="bottom"
              class="absolute left-1/2 top-1/2 inline-flex h-8.5 max-w-[52vw] -translate-x-1/2 -translate-y-1/2 items-center gap-1.5 whitespace-nowrap rounded-[var(--r)] border border-line2 bg-panel2 px-3.25 text-sm font-semibold text-ink"
            >
              <span class="truncate">{{ currentSectionLabel() }}</span>
              <svg
                class="size-4 flex-none text-c3"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>
            <span class="flex-1"></span>
            <!-- Та же дверь к урокам, что и в десктопной шапке, и та же кнопка: нажатие сразу
                 открывает окно уроков — меню помощи в кабинете нет вовсе (R17). Якоря коуча у неё
                 здесь нет: карточки приходят в десктопную шапку. -->
            @if (helpAvailable()) {
              <onboarding-lessons-door (open)="openLibrary()" />
            }
            <!-- the same bell + avatar pair as the desktop header -->
            <div class="flex items-center gap-2">
              <app-notifications-bell />
              <app-fotoclass-account-slot />
            </div>
          </div>
        </header>
      }

      <ng-container hlmAppShellLogo>
        <!-- Телефон организатора несёт только название проекта: логотипа в его шапке нет (s1-ac6). -->
        <!-- Вордмарк ведёт на главную кабинета одним переходом (homeLink); у организатора и до ответа
             партнёрской проверки он статичен. Меню продуктов вместо ссылки включает сам переключатель
             (PRODUCT_MENU_ENABLED), шапка лишь называет свой продукт. -->
        @if (!organizerMobileBar()) {
          <hlm-product-switcher currentProductId="fotoclass" [homeLink]="logoHomeLink()" />
        }
        @if (!isOrganizer()) {
          <!-- Мобильная дверь в те же табы: у организатора табов нет, поэтому и двери нет. -->
          <!-- phone opens a sheet like the in-project ☰; tablet keeps the dropdown -->
          @if (isMobile()) {
            <button
              type="button"
              (click)="navSheetOpen.set(true)"
              aria-label="Навигация"
              class="relative ml-2 grid size-8 shrink-0 place-items-center rounded-lg text-c2 after:absolute after:-inset-1.5 after:content-[''] hover:bg-panel hover:text-ink lg:hidden"
            >
              <app-icon name="menu" [size]="18" />
            </button>
          } @else {
            <button
              [hlmDropdownMenuTrigger]="navMenu"
              align="start"
              side="bottom"
              aria-label="Навигация"
              class="relative ml-2 grid size-8 shrink-0 place-items-center rounded-lg text-c2 after:absolute after:-inset-1.5 after:content-[''] hover:bg-panel hover:text-ink lg:hidden"
            >
              <app-icon name="menu" [size]="18" />
            </button>
          }
        }
        @if (isOrganizer() && projectId()) {
          <!-- Организатор носит название проекта по центру шапки (s1-ac8): табов у него нет, и центр
               свободен. Абсолют считается от sticky-шапки, поэтому середина — истинная середина бара,
               а не остаток флекса логотипа (тот же приём, что у мобильной пилюли выше). На телефоне
               центр занят: справа стоят «Отправить» и бургер, поэтому название едет в поток слева и
               обрезается по месту (s1-ac6). -->
          <span
            class="truncate text-base font-semibold tracking-[-0.01em] text-ink"
            [class]="
              organizerMobileBar()
                ? 'min-w-0'
                : 'absolute left-1/2 top-1/2 max-w-[var(--max-w-header-title)] -translate-x-1/2 -translate-y-1/2'
            "
          >
            {{ projectName() ?? 'Проект' }}
          </span>
        }
        @if (projectId() && !isOrganizer()) {
          <div class="ml-2 flex shrink-0 items-center border-l border-line2 pl-2 md:ml-3.5 md:pl-3.5">
            <a
              hlmBtn
              routerLink="/projects"
              aria-label="к проектам"
              variant="ghost"
              class="relative h-7.5 gap-2 border border-transparent px-2 after:absolute after:inset-x-0 after:-inset-y-2 after:content-[''] hover:border-line2 md:px-2.5"
            >
              <hlm-app-shell-back-link label="к проектам">
                <app-icon name="chevron-left" [size]="14" />
              </hlm-app-shell-back-link>
            </a>
          </div>
        }
      </ng-container>

      <ng-container hlmAppShellActions>
        <!-- Одна дверь для всех ролей: иконка стоит левее кнопок-действий и меню аккаунта, поэтому живёт
             ВНЕ ролевых веток — на каждом экране с шапкой LUMIR PHOTO она на одном и том же месте. -->
        <!-- На телефоне организатора этой двери в шапке нет: там остаются только название, «Отправить»
             и сам бургер (s1-ac6, s1-ac6.1). -->
        <!-- Отправка группе на десктопе организатора стоит в шапке справа от знака вопроса: с экрана
             «Ученики» кнопку убрали, чтобы список начинался сразу с фильтров. -->
        @if (!organizerMobileBar()) {
          <!-- Дверь к урокам — близнец аватара: тот же кружок 32 px с волосяной рамкой, и нажатие
               сразу открывает окно уроков, без промежуточного меню. Подсказки экрана и связь с
               поддержкой живут теперь в меню аккаунта и в доке окна уроков (R11). Организатору двери
               не положено: аудиторию считает сам онбординг, своей ролевой проверки у шапки нет.
               Отрицательного поля больше нет — у кружка свои края, и до соседа остаётся ряд (12 px). -->
          @if (helpAvailable()) {
            <onboarding-lessons-door anchor="library-door" (open)="openLibrary()" />
          }
        }
        @if (isOrganizer() && !organizerMobileBar()) {
          <button hlmBtn type="button" variant="default" size="action" class="shrink-0" (click)="openShareForOrganizer()">
            Отправить группе
          </button>
        }
        @if (isOrganizer()) {
          @if (organizerMobileBar()) {
            <button hlmBtn type="button" variant="default" size="action" class="shrink-0" (click)="openShareForOrganizer()">
              Отправить
            </button>
            <button
              type="button"
              (click)="navSheetOpen.set(true)"
              aria-label="Меню проекта"
              class="grid size-9 shrink-0 place-items-center rounded-lg text-ink active:bg-panel pointer-coarse:size-11"
            >
              <app-icon name="menu" [size]="20" />
            </button>
          } @else {
            <!-- Тот же аватар с меню, что у фотографа: у организатора в меню ровно «Выйти» и тема. -->
            <!-- Аккаунта у организатора нет, поэтому в шапке меню стоит подпись роли, а не имя. -->
            <!-- Предпросмотр — это владелец чужими глазами: шапка та же, но выхода из аккаунта в меню
                 нет, из режима выводит «Выйти из режима» в баннере. -->
            <app-account-menu nameOverride="Организатор" [showLogout]="!isOrganizerPreview()" (logoutConfirmed)="logout()">
              <app-theme-segment accountMenuFooter [mode]="themeMode()" (modeSelected)="setTheme($event)" />
            </app-account-menu>
          }
        } @else {
          @if (projectId(); as pid) {
            <app-project-download-button [projectId]="pid" [coachAnchor]="downloadCoachAnchor()" />
            <!-- якорь: список альбомов → кнопка «Заказчику» -->
            <button
              hlmBtn
              type="button"
              variant="default"
              size="action"
              class="relative shrink-0 after:absolute after:inset-x-0 after:-inset-y-2 after:content-[''] md:px-5.5"
              [onboardingAnchor]="classLinkCoachAnchor()"
              (click)="openShare(pid)"
            >
              <app-icon name="arrow-up" [size]="15" />
              <span class="hidden min-compact:inline">Заказчику</span>
            </button>
          }
          <!-- Bell + avatar as one pair, the same as studio-manager: the actions slot spaces by gap-3, the pair by gap-2. -->
          <div class="flex items-center gap-2">
            <app-notifications-bell />
            <app-fotoclass-account-slot />
          </div>
        }
      </ng-container>

      @if (isOrganizerPreview()) {
        <app-preview-banner
          hlmAppShellBanner
          class="sticky top-[var(--app-shell-header-h)] z-[19]"
          text="Вы смотрите проект глазами организатора."
          exitLabel="Выйти из режима"
          (exit)="exitOrganizerPreview()"
        />
      }

      <ng-template #sectionMenu>
        <div hlmDropdownMenu class="min-w-50">
          @for (item of navItems(); track item.label) {
            <!-- gap-0: the count carries its own 4px (ml-1), as on the desktop tab -->
            <a
              hlmDropdownMenuItem
              class="gap-0"
              [routerLink]="item.link"
              [queryParams]="item.queryParams ?? null"
              routerLinkActive="text-acc font-semibold"
            >
              <span>{{ item.label }}</span>
              <hlm-app-shell-count [item]="item" />
            </a>
          }
        </div>
      </ng-template>

      <ng-template #navMenu>
        <div hlmDropdownMenu class="min-w-47.5">
          @for (item of navItems(); track item.label) {
            <a
              hlmDropdownMenuItem
              class="gap-0"
              [routerLink]="item.link"
              [queryParams]="item.queryParams ?? null"
              routerLinkActive="text-acc font-semibold"
              (click)="onTabClick(item.label)"
            >
              <span>{{ item.label }}</span>
              <hlm-app-shell-count [item]="item" />
            </a>
          }
        </div>
      </ng-template>

      <router-outlet />
    </hlm-app-shell>
    <!-- Переход из кабинета ждёт гвард под шапкой: шапка остаётся видимой и кликабельной. Без шапки
         (модальный или мобильный мастер) вариант снят: полоса над ним открыла бы старую страницу, а
         полноэкранная корневая вуаль закрывает всё. -->
    @if (headerShown()) {
      <hlm-route-veil inset="below-header" />
    }

    <!-- ═══ fullscreen nav drawer: exit → sections → send/download ═══ -->
    <!-- gated on the mobile bar too: it is the only opener, and the fixed sheet would otherwise stay
         pinned over the shared desktop header when a rotation to landscape brings that header back -->
    @if (navSheetOpen() && mobileProjectBar()) {
      <hlm-bottom-sheet initialState="full" (stateChange)="onNavSheetState($event)">
        <a
          routerLink="/projects"
          (click)="closeNavSheet()"
          class="flex items-center gap-2.5 pb-3 pt-1 text-base font-semibold text-ink pointer-coarse:min-h-11"
        >
          <span class="grid size-6 place-items-center rounded-md bg-panel text-c2">
            <app-icon name="chevron-left" [size]="15" />
          </span>
          К списку проектов
        </a>
        <div class="border-t border-line2"></div>
        <div class="truncate pb-1 pt-3.5 text-xs font-semibold uppercase tracking-[0.04em] text-c3">
          {{ projectName() ?? 'Проект' }}
        </div>
        @for (item of navItems(); track item.label) {
          <a
            [routerLink]="item.link"
            [queryParams]="item.queryParams ?? null"
            routerLinkActive="!text-acc font-semibold"
            (click)="closeNavSheet()"
            class="flex h-11 items-center rounded-[var(--r)] px-1.5 text-sm font-medium text-c2"
          >
            <span>{{ item.label }}</span>
            <hlm-app-shell-count [item]="item" />
          </a>
        }
        @if (projectId(); as pid) {
          <div class="mt-2 border-t border-line2 pt-2">
            <button type="button" (click)="shareFromDrawer(pid)" [class]="drawerActionClass">
              <app-icon name="arrow-up" [size]="16" tone="c3" />
              Отправить заказчику
            </button>
            <button type="button" (click)="downloadFromDrawer(pid)" [class]="drawerActionClass">
              <app-icon name="arrow-down" [size]="16" tone="c3" />
              Скачать альбомы
            </button>
          </div>
        }
      </hlm-bottom-sheet>
    }

    <!-- ═══ phone ≡ outside a project: the cabinet tabs, same rows as the project drawer ═══ -->
    @if (navSheetOpen() && isMobile() && !projectId()) {
      <hlm-bottom-sheet (stateChange)="onNavSheetState($event)">
        @for (item of navItems(); track item.label) {
          <a
            [routerLink]="item.link"
            [queryParams]="item.queryParams ?? null"
            routerLinkActive="!text-acc font-semibold"
            (click)="onTabClick(item.label); closeNavSheet()"
            class="flex h-11 items-center rounded-[var(--r)] px-1.5 text-sm font-medium text-c2"
          >
            <span>{{ item.label }}</span>
            <hlm-app-shell-count [item]="item" />
          </a>
        }
      </hlm-bottom-sheet>
    }

    <!-- ═══ бургер организатора: те же двери, что у него в десктопной шапке (s1-ac6.1) ═══ -->
    @if (navSheetOpen() && organizerMobileBar()) {
      <hlm-bottom-sheet (stateChange)="onNavSheetState($event)">
        <div class="flex h-11 items-center px-1.5"><app-theme-toggle /></div>
        @if (!isOrganizerPreview()) {
          <button type="button" (click)="logout()" [class]="drawerActionClass">
            <span class="inline-flex shrink-0 rotate-90"><app-icon name="arrow-up" [size]="16" tone="c2" /></span>
            Выйти
          </button>
        }
        <!-- Линию фильтров считает экран «Ученики», бургер её только показывает и переключает: тот же
             выбор, что и в десктопной линии вкладок, поэтому повторный тап по активному снимает фильтр. -->
        @if (organizerFilterTabs().length > 0) {
          <div class="mt-2 border-t border-line2 pt-2">
            @for (tab of organizerFilterTabs(); track tab.label) {
              <button type="button" (click)="pickFilterFromDrawer(tab.badge)" [class]="drawerActionClass">
                <span
                  class="min-w-0 flex-1 truncate text-left"
                  [class.text-ink]="organizerFilter() !== tab.badge"
                  [class.text-acc]="organizerFilter() === tab.badge"
                  >{{ tab.label }}</span
                >
                <span
                  class="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-panel3 px-1.5 text-xs font-semibold tabular-nums text-c2"
                  >{{ tab.count }}</span
                >
              </button>
            }
          </div>
        }
      </hlm-bottom-sheet>
    }

    <app-download-progress-host />
  `,
})
export class ShellComponent {
  private readonly router = inject(Router);
  private readonly roles = inject(ProjectRoleService);
  private readonly supportWidget = inject(SupportWidgetService);
  private readonly partnerStudios = inject(PartnerStudioResource);
  private readonly onboarding = inject(OnboardingStateService);
  private readonly projects = inject(ProjectsFacade);
  private readonly defaultPrintProduct = inject(DefaultPrintProductService);
  private readonly createProjectPreferences = inject(CreateProjectPreferencesService);
  private readonly draftStash = inject(CreateProjectDraftStashService);
  private readonly projectCreation = inject(ProjectCreationService);
  private readonly createEntry = inject(CreateEntryService);
  private readonly authAnalytics = inject(AuthAnalyticsService);
  private readonly uiAnalytics = inject(UiAnalyticsService);
  private readonly dialog = inject(BrnDialogService);
  private readonly shareLauncher = inject(OrganizerShareLauncherService);
  private readonly downloadLauncher = inject(ProjectDownloadLauncherService);
  private readonly balancePopups = inject(BalancePopupsLauncherService);
  private readonly templateGranted = inject(TemplateGrantedLauncherService);
  private readonly retouchDone = inject(RetouchDoneLauncherService);
  private readonly retouchAccess = inject(RetouchAccessService);
  // Движок онбординга — не мастер: `onboarding` выше держит состояние мастера альбома, а этот сервис
  // знает про уроки и аудиторию.
  private readonly lessons = inject(OnboardingService);
  // Что на экране альбомов уже можно, знает сама страница — шапка только читает её состояние.
  private readonly albumsCoach = inject(AlbumsCoachState);
  private readonly theme = inject(ThemeService);
  protected readonly isMobile = inject(IS_MOBILE);
  private readonly wizardChrome = inject(WizardChromeService);
  private readonly vcr = inject(ViewContainerRef);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly drawerActionClass =
    'flex h-11 w-full items-center gap-2.5 rounded-[var(--r)] px-1.5 text-sm font-medium text-c2';

  private readonly url = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(e => e.urlAfterRedirects),
    ),
    { initialValue: this.router.url },
  );

  protected readonly projectId = computed(() => {
    const match = this.url().match(/^\/projects\/([^/?#]+)/);
    // /projects/new is the create-project wizard, not a project — keep the cabinet topbar there.
    return match && match[1] !== 'new' ? match[1] : null;
  });

  // Project whose settings read has settled (or timed out): the retouch switch of its tab row is known.
  private readonly settingsReadSettledFor = signal<string | null>(null);

  // On the settings screen the proto reveals a transient, routeless «Настройки» tab in the
  // active state and suppresses the CTA.
  protected readonly isAccount = computed(() => /^\/account(?:[/?#]|$)/.test(this.url()));

  // Partner-hood belongs to the ACCOUNT, not to the address bar: read off the studio binding the
  // `/partner` guard and the root redirect already ask for. Deriving it from the URL prefix stranded a
  // partner who walked into «Аккаунт» — the cabinet tabs vanished and only a typed URL led back.
  // Three states, not two: `undefined` is «the account has not answered yet». A `false` default painted
  // the photographer's «Проекты/Шаблоны» in the first frame and swapped them for «Продажи/Шаблоны» a
  // moment later, which is the tab flicker the cabinet was reported for.
  protected readonly isPartner = computed<boolean | undefined>(() => {
    const studio = this.partnerStudios.studio();
    return studio === undefined ? undefined : !!studio;
  });

  // A coordinator-only session gets the trimmed organizer chrome (R1): no photographer surfaces.
  protected readonly isOrganizer = this.roles.isOrganizerOnly;

  // True when the trimmed chrome is an owner PREVIEWING the organizer view — not a real coordinator.
  protected readonly isOrganizerPreview = this.roles.isOrganizerPreview;

  protected readonly projectName = this.projects.currentProjectName;

  // Кому положена дверь к урокам, решает онбординг: у организатора аудитории нет — значит нет и
  // двери, ни в шапке, ни в проектном баре. Уроков может не быть ни одного — дверь всё равно нужна:
  // в окне уроков остаётся док поддержки.
  protected readonly helpAvailable = this.lessons.audience;

  // Съёмки кабинета — половина правила новичка, которую знает только приложение: съёмка это проект
  // больше чем с 10 фотографиями, демо-копия не в счёт — считаются только свои проекты. Пока список
  // ни разу не ответил, вердикта нет вовсе: пустой стор — это «ещё не знаем», а не «проектов нет».
  // Projects in «Архив» count too: until that shelf is read (or failed) the verdict waits for it.
  private readonly shootCount = computed<number | null>(() => {
    if (!this.projects.projectsLoaded()) return null;
    const shelfPending =
      this.projects.putAwayCount() > 0 && this.projects.putAwayProjects().length === 0 && !this.projects.putAwayLoadFailed();
    if (shelfPending) return null;
    return this.projects
      .knownProjects()
      .filter(project => !isDemoProject(project) && (project.counter?.photos ?? 0) > SHOOT_PHOTOS).length;
  });

  // Якоря шапки: «Заказчику» стоит в обоих режимах — кнопка есть и там, где альбомы собирает сам
  // фотограф, — а «Скачать» появляется после первого утверждённого альбома. Экран проверяет сам
  // движок — шапка адреса не читает.
  protected readonly classLinkCoachAnchor = computed(() => (this.albumsCoach.projectId() ? 'albums-send-class-link' : null));

  // Единственная подсказка, которой в демо-проекте нет: скачивание там закрыто (DemoDownloadGuardService
  // отвечает на клик уведомлением), а карточка коуча не зовёт к действию, которого нет.
  protected readonly downloadCoachAnchor = computed(() =>
    this.albumsCoach.hasApproved() && !this.albumsCoach.isDemo() ? 'albums-download' : null,
  );

  // The wordmark leads straight to the account's main page in one navigation — never through the '/'
  // redirect. The coordinator's chrome has no photographer list and a previewing owner would land in it
  // wearing the organizer chrome, so their wordmark stays static; an unanswered partner check waits.
  protected readonly logoHomeLink = computed<string | null>(() => {
    if (this.isOrganizer()) return null;
    const partner = this.isPartner();
    if (partner === undefined) return null;
    return partner ? '/partner' : '/projects';
  });

  // Фильтры экрана «Ученики» — их считает сам экран, бургер организатора только читает (s1-ac6.1).
  protected readonly organizerFilterTabs = this.projects.organizerFilterTabs;
  protected readonly organizerFilter = this.projects.organizerFilter;

  // Editor-mode tabs (Редактор/Дизайнер/Превью) live in the album editor's own top bar — contextual, not at project level.
  protected readonly navItems = computed<NavItem[]>(() => {
    const pid = this.projectId();
    // A project with no generated albums hides the «Альбомы» tab for both the organizer view and the
    // photographer/owner view — there is nothing to show there yet.
    const albumsTab: NavItem[] = this.projects.hasAlbums() ? [{ label: 'Альбомы', link: ['/projects', pid, 'albums'] }] : [];
    // Организатор живёт на одном экране («Ученики» на корне проекта) — навигации у него нет вовсе (s1-ac1).
    if (pid && this.isOrganizer()) return [];

    if (pid) {
      // «Альбомы» hangs on async reads; a tab arriving late re-centres the row under a pointer mid-press and
      // the click lands on the row, not on «Фото». The row waits for albums and settings; «Ретушь» follows the
      // project's retouch setting, never the slow retouch scope.
      const albumsKnown = pid in this.projects.albumsByProject() || this.projects.albumsLoadFailed();
      const settingsKnown = this.settingsReadSettledFor() === pid || pid in this.projects.baseSettingsByProject();
      if (!albumsKnown || !settingsKnown) return [];
      const retouchTab: NavItem[] = this.retouchAccess.sectionVisible()
        ? // якорь: проект → вкладка «Ретушь» в навигации проекта
          [{ label: 'Ретушь', link: ['/projects', pid, 'retouch'], coachAnchor: 'retouch-section' }]
        : [];
      // «Покупки» where upsale is on, or switched off with bills still owed (the list row's debt counter): an
      // unread or failed read shows no tab rather than an empty page.
      const upsaleDue = this.projects.projectById(pid)?.counter?.upsale?.due ?? 0;
      const purchasesTab: NavItem[] =
        this.projects.upsaleByProject()[pid]?.enabled || upsaleDue > 0
          ? [{ label: 'Покупки', link: ['/projects', pid, 'purchases'] }]
          : [];
      const total = this.projects.commentCounts()?.total;
      return [
        ...albumsTab,
        { label: 'Лица', link: ['/projects', pid, 'faces'] },
        { label: 'Фото', link: ['/projects', pid, 'photos'] },
        ...retouchTab,
        ...purchasesTab,
        {
          label: 'Чат',
          link: ['/projects', pid, 'messages'],
          count: total || undefined,
          countStyle: 'comments',
          countLabel: total ? `${total} ${pluralRu(total, OPEN_FORMS)}` : undefined,
        },
      ];
    }

    // Which cabinet this is is still unknown: an empty tab strip costs one beat, the wrong strip costs a
    // visible swap. The `/partner` guard and the root redirect await the very same answer, so on those
    // landings it is already in hand and the tabs come up with the page.
    if (this.isPartner() === undefined) return [];

    // The partner cabinet is a surface of its own: the photographer's «Проекты»/«Шаблоны» lead nowhere
    // there. It sits BELOW the project branches so a bound partner opening a project still gets that
    // project's tabs.
    if (this.isPartner()) {
      return [
        { label: 'Продажи', link: ['/partner/sales'] },
        { label: 'Шаблоны', link: ['/partner/templates'] },
      ];
    }

    return [
      { label: 'Проекты', link: ['/projects'] },
      { label: 'Шаблоны', link: ['/designs'] },
    ];
  });

  // The settings screen is reached from the avatar menu, not from a tab — the shell surfaces a
  // «Настройки» tab only while you are there, so routerLinkActive marks it like any other.
  protected readonly shellTabs = computed<NavItem[]>(() =>
    this.isAccount() ? [...this.navItems(), { label: 'Настройки', link: ['/account'] }] : this.navItems(),
  );

  // ── mobile in-project bar (Variant B) ──
  protected readonly mobileProjectBar = computed(() => this.isMobile() && !!this.projectId() && !this.isOrganizer());

  // Телефон организатора: шапка остаётся общей, меняется только её наполнение — название, «Отправить»
  // и бургер, всё остальное уходит в бургер (s1-ac6).
  protected readonly organizerMobileBar = computed(() => this.isMobile() && this.isOrganizer() && !!this.projectId());

  // The standalone wizards own the whole phone screen with their one-line strip — the cabinet
  // topbar would stack a dead second bar above it.
  protected readonly hideTopbarForMobileWizard = computed(
    () =>
      this.isMobile() &&
      (this.url().startsWith('/projects/new') ||
        this.url().startsWith('/designs/new') ||
        this.url().startsWith('/partner/templates/new')),
  );

  // Every screen that swaps in its own header — or wants none at all — suppresses the shared one
  // instead of forking the shell.
  // A wizard in the modal layout draws its own exit chrome over the bare page.
  protected readonly hideStandardHeader = computed(
    () => this.mobileProjectBar() || this.hideTopbarForMobileWizard() || this.wizardChrome.modal(),
  );

  /** Сверху стоит шапка той же высоты: общая или мобильная полоса проекта. */
  protected readonly headerShown = computed(() => this.mobileProjectBar() || !this.hideStandardHeader());

  protected readonly currentSectionLabel = computed(() => {
    const url = this.url();
    if (url.includes('/photos')) return 'Фото';
    if (url.includes('/retouch')) return 'Ретушь';
    if (url.includes('/faces')) return 'Лица';
    if (url.includes('/messages')) return 'Чат';
    if (url.includes('/purchases')) return 'Покупки';
    if (url.includes('/albums')) return 'Альбомы';
    return 'Проект';
  });

  // Тема в меню организатора — тот же сегмент, что в меню фотографа: сервис резолвит 'system' сам.
  protected readonly themeMode = computed<ThemeSegmentMode>(() => this.theme.resolvedTheme());

  protected readonly navSheetOpen = signal(false);

  constructor() {
    // The balance windows are cabinet-only, and this shell is the one surface the onboarding wizard,
    // the design tunnel and the album editor never mount — so registering here is the whole gate.
    this.balancePopups.registerCabinet(this.vcr, this.destroyRef);
    // Same gate for the partner-licence window: it announces a template that landed between visits.
    this.templateGranted.registerCabinet(this.vcr, this.destroyRef);
    // And for the end of a retouch run: it waits on every cabinet page until closed.
    this.retouchDone.registerCabinet(this.vcr, this.destroyRef);
    // И тот же вход — для приветствия новичка: в редакторах оболочки кабинета нет, и оно там не всплывает.
    // Ушла оболочка — ушёл и кабинет: отложенное окно новичка в редакторе не поднимется.
    this.lessons.noteCabinet(this.shootCount);
    this.destroyRef.onDestroy(() => this.lessons.leaveCabinet());
    // A photographer's landing (/projects, /designs, /account) passes no partner guard, so the shell is
    // the one that has to ask — the tabs stay empty until the account answers. The ask is memoized.
    void this.partnerStudios.myStudio();
    this.destroyRef.onDestroy(() => (this.shellDestroyed = true));
    // «Вернуться к созданию проекта» в разделе «Шаблоны»: тот же вход, что у кнопки кабинета, — шаг мастера
    // (на телефоне шторка) забирает черновик сам, чтобы это была ровно одна дорога назад.
    effect(() => {
      if (!this.draftStash.pendingReturn()) return;
      untracked(() => void this.openCreateProject());
    });
    this.createEntry.openProject$.pipe(takeUntilDestroyed()).subscribe(entry => void this.openCreateProject(entry?.designId));
    // One project-room watch per open project, on every project route: a status written elsewhere (the client
    // signing on their own device) reaches the album list, the retouch tab and «Фото» without a reload.
    effect(onCleanup => {
      const pid = this.projectId();
      if (!pid) return;
      onCleanup(untracked(() => this.projects.watchAlbumPreviews(pid)));
    });
    // A project on no loaded list (past page 1 of «Архив», opened by link) is known by its detail.
    effect(() => {
      const pid = this.projectId();
      if (!pid || !this.projects.projectsLoaded() || this.projects.putAwayLoading() || this.projects.projectById(pid)) return;
      untracked(() => void this.projects.loadProjectDetail(pid));
    });
    // «Ретушь» tab and section read the settings switch and the scope: loaded once per project entry,
    // photographer only (the coordinator has no sections, the scope route is owner-only).
    effect(() => {
      const pid = this.projectId();
      if (!pid || this.isOrganizer()) return;
      untracked(() => {
        void this.projects.loadRetouchScope(pid);
        void withTimeout(this.projects.loadProjectBaseSettings(pid), ACCESS_READ_LIMIT_MS)
          .catch(() => undefined)
          .then(() => {
            if (this.projectId() === pid) this.settingsReadSettledFor.set(pid);
          });
      });
    });
  }

  protected onNavSheetState(state: 'collapsed' | 'half' | 'full'): void {
    if (state === 'collapsed') setTimeout(() => this.navSheetOpen.set(false), motionDurationMs('--t-mid'));
  }

  protected closeNavSheet(): void {
    this.navSheetOpen.set(false);
  }

  protected shareFromDrawer(projectId: string): void {
    this.closeNavSheet();
    this.openShare(projectId);
  }

  protected pickFilterFromDrawer(badge: string | null): void {
    this.projects.setOrganizerFilter(badge);
    this.closeNavSheet();
  }

  // Мобильная «Отправить» — то же окно, что и кнопка «Отправить группе» на экране организатора.
  protected openShareForOrganizer(): void {
    const pid = this.projectId();
    if (pid) this.shareLauncher.openForOrganizer(pid);
  }

  protected downloadFromDrawer(projectId: string): void {
    this.closeNavSheet();
    this.downloadLauncher.open(this.vcr, projectId);
  }

  protected onTabClick(label: string): void {
    // Analytics wants the route key (`projects`, `faces`, `account`), not the localized label.
    const link = this.shellTabs().find(t => t.label === label)?.link;
    const parts = String(Array.isArray(link) ? link[link.length - 1] : (link ?? '')).split('/');
    const key = parts[parts.length - 1];
    this.uiAnalytics.tabNavigated(key || label);
  }

  protected async logout(): Promise<void> {
    this.uiAnalytics.logout('shell');
    this.supportWidget.stop();
    this.authAnalytics.clear();
    this.roles.clear();
    clearAuth();
    if (isBffMode()) {
      // Server-side BFF session must die too — otherwise the next probe still reports 'alive'.
      // No returnUrl: a logged-out user should land on the login screen, not bounce back in.
      await bffLogout();
      forceRedirectToBffLogin();
      return;
    }

    // An invited coordinator has no account — routing to '/' would bounce through the guard to the
    // identity login (a dead end for them). Send them to the public landing instead.
    window.location.assign(environment.LANDING_URL);
  }

  // Leave the organizer preview — the owner's real session is untouched; drop the tab-local flag
  // and return to the very project the preview was opened on (not the projects list).
  protected async exitOrganizerPreview(): Promise<void> {
    const pid = this.projectId();
    // The surface the preview was opened from (threaded through the coordinator link) — read it
    // BEFORE exit clears it, so «Выйти из режима» returns exactly where «Предпросмотр» started.
    const returnUrl = this.roles.previewReturn();
    this.roles.exitOrganizerPreview();
    if (returnUrl) {
      void this.router.navigateByUrl(returnUrl);
      return;
    }

    // No stored origin: the owner may be mid-onboarding — leaving the preview must respect the gate,
    // not dump them in a cabinet they can't yet reach. An onboarded owner (or a failed lookup) returns
    // to the project as before.
    const resume = await this.onboarding.resolve(pid ?? undefined);
    if (resume && !resume.onboarded) {
      const { commands, extras } = resumeNavigation(resume.target);
      void this.router.navigate(commands, extras);
      return;
    }

    void this.router.navigate(pid ? ['/projects', pid, 'faces'] : ['/projects']);
  }

  protected setTheme(mode: ThemeSegmentMode): void {
    if (this.theme.resolvedTheme() !== mode) this.uiAnalytics.themeSwitched(mode);
    this.theme.setPreference(mode);
  }

  // Тот же вызов, что и пункт «Видео-обзоры» в меню помощи: окно уроков открывается с начала списка.
  protected openLibrary(): void {
    this.lessons.openLibrary();
  }

  protected openShare(projectId: string): void {
    void this.shareLauncher.open(this.vcr, projectId);
  }

  private async openCreateProject(designId?: string): Promise<void> {
    // The catalog await below keeps the CTA live and un-backdropped for up to its ceiling: without this
    // single-flight two clicks resume into two dialogs, and each accepted draft makes its own project.
    if (this.openingCreateProject) return;
    this.openingCreateProject = true;
    try {
      // Warm the wizard's lazy chunk while the user fills the form — the post-modal entry must be instant.
      void import('../features/projects/feature/project-wizard-page.component');
      // Open from one coherent snapshot: both the catalog and the user's server preference are bounded,
      // so either unavailable dependency falls back without eating the click.
      await Promise.all([this.defaultPrintProduct.prefetchCatalogBounded(), this.createProjectPreferences.prefetchBounded()]);
      // A route outside the cabinet tears this shell down while the catalog is still in flight — the
      // `vcr` below belongs to a destroyed view by then, and the dialog would overlay the new route.
      if (this.shellDestroyed) return;
      // md+ — «Новый проект» открывается шагом мастера: он сам забирает черновик возврата из «Шаблонов».
      if (!this.isMobile()) {
        void this.router.navigate(['/projects/new'], { queryParams: { creating: 1, ...(designId ? { design: designId } : {}) } });
        return;
      }

      // Пак входа проверяется по спискам: он даёт шторке и шаблон, и формат.
      const pack = designId ? await this.createEntry.resolvePack(designId) : null;
      if (this.shellDestroyed) return;
      // Возврат из «Шаблонов» открывает эту же модалку на оставленном черновике; снимок отдаётся один раз,
      // и любое другое открытие (кнопка кабинета) получает пустой лист. Забираем его ТОЛЬКО когда модалка
      // действительно открывается: съеденный до await'а снимок пропадал бы вместе с уходом с экрана, а
      // «Вернуться», нажатое пока летит каталог, попадает ровно в это открытие.
      const restore = pack ? null : this.draftStash.takeReturn();
      // restoreFocus OFF: the closing Enter keypress would re-activate the CTA and re-open the modal.
      const ref = this.dialog.open<CreateProjectDialogContext | undefined, CreateProjectDraft>(
        CreateProjectDialogComponent,
        this.vcr,
        pack ? pack : restore ? { restore } : undefined,
        { hasBackdrop: true, restoreFocus: false },
      );
      ref.closed$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe(draft => {
        if (!draft) return;
        this.projectCreation.enterProjectWizard(draft);
      });
    } finally {
      // Released once the dialog is really up — from here its backdrop owns the CTA.
      this.openingCreateProject = false;
    }
  }

  private openingCreateProject = false;
  private shellDestroyed = false;
}
